import React, { useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { push } from 'redux-first-history';
import { init } from '@sentry/react';
import loadable from '@loadable/component';

import _package from '../../package.json';
import ROUTES from '../constants/routes';
import icons from '../assets/icons';
import { authenticateUser } from '../store/user';
import { setLoader } from '../store/pages';
import Terms from './App/TermsAndPrivacy/Terms';
import Privacy from './App/TermsAndPrivacy/Privacy';
import { handleAuthCode } from '../helpers/auth';
import { getTokens, setTokens } from '../helpers/auth/tokens';

const Authentication = loadable(() => import('./Authentication'));
const Layout = loadable(() => import('./App/Layout'));
const Portfolio = loadable(() => import('./App/Portfolio'));
const Organization = loadable(() => import('./App/Organization'));
const Site = loadable(() => import('./App/Site'));
const Meter = loadable(() => import('./App/Meter'));
const Profile = loadable(() => import('./App/Profile'));
const GridDetails = loadable(() => import('./App/GridDetails'));
const Kiosk = loadable(() => import('./Kiosk'));

const APP_STR = ROUTES.AUTH.INDEX + '/';
icons();

if (['staging', 'prod', 'production'].includes(process.env.REACT_APP_ENV)) {
  init({
    dsn: 'https://c47e0459cd154f089122dc8072d07c2b@o240785.ingest.sentry.io/4505387455938560',
    environment: process.env.REACT_APP_ENV,
    release: _package.version,
  });
}

let didInit = false;

function AppRoutes() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handleAuthCodeGrant = async (code) => {
      try {
        await handleAuthCode(code);
        setSearchParams({});
        dispatch(authenticateUser());
      } catch (err) {
        console.error(err.message);
        dispatch(push(ROUTES.UNAUTH.LOGIN));
        dispatch(setLoader(false));
      }
    };

    const handleRefreshToken = () => {
      try {
        const _refreshToken = searchParams.get('refreshToken');
        if (_refreshToken) {
          setTokens({ RefreshToken: _refreshToken });
        }

        const { refreshToken } = getTokens();
        if (refreshToken) {
          dispatch(authenticateUser());
        } else {
          dispatch(push(ROUTES.UNAUTH.LOGIN));
          dispatch(setLoader(false));
        }
      } catch (err) {
        console.error(err);
        dispatch(push(ROUTES.UNAUTH.LOGIN));
        dispatch(setLoader(false));
      }
    };

    // React.Strict renders twice in dev
    // didInit avoids trying to authenticate twice
    if (!didInit) {
      didInit = true;
      if (!location.pathname.startsWith(ROUTES.UNAUTH.KIOSK)) {
        const code = searchParams.get('code');
        dispatch(setLoader(true));
        if (code) {
          handleAuthCodeGrant(code);
        } else {
          handleRefreshToken();
        }
      }
    }

    // eslint-disable-next-line
  }, []);

  return (
    <div className='App'>
      <Routes>
        <Route
          path={ROUTES.UNAUTH.LANDING}
          element={<Navigate to={ROUTES.UNAUTH.LOGIN} />}
        />
        <Route path={ROUTES.UNAUTH.LOGIN} element={<Authentication />} />
        <Route path={ROUTES.UNAUTH.KIOSK} element={<Kiosk />} />

        <Route path={ROUTES.AUTH.INDEX} element={<Layout />}>
          <Route
            path={ROUTES.AUTH.INDEX}
            element={<Navigate to={ROUTES.AUTH.PORTFOLIO} />}
          />
          <Route
            path={ROUTES.AUTH.PORTFOLIO.replace(APP_STR, '')}
            element={<Portfolio />}
          />
          <Route
            path={ROUTES.AUTH.ORGANIZATION.replace(APP_STR, '')}
            element={<Organization />}
          />
          <Route
            path={ROUTES.AUTH.SITE.replace(APP_STR, '')}
            element={<Site />}
          />
          <Route
            path={ROUTES.AUTH.METER.replace(APP_STR, '')}
            element={<Meter />}
          />
          <Route
            path={ROUTES.AUTH.PROFILE.replace(APP_STR, '')}
            element={<Profile />}
          />
          <Route
            path={ROUTES.AUTH.GRID_DETAILS.replace(APP_STR, '')}
            element={<GridDetails />}
          />
          <Route
            path={ROUTES.AUTH.TERMS.replace(APP_STR, '')}
            element={<Terms />}
          />
          <Route
            path={ROUTES.AUTH.PRIVACY.replace(APP_STR, '')}
            element={<Privacy />}
          />
        </Route>
        <Route path={'*'} element={<Navigate to={ROUTES.UNAUTH.LOGIN} />} />
      </Routes>
    </div>
  );
}

export default AppRoutes;
